import type { AuthUser } from '@/lib/auth/auth-context'
import type { OrganisationData } from '@jobilla/nexus-sdk'

export type OnboardingUser = AuthUser & {
	// Nexus falls back to a default language, so `language` is always set and can't tell whether
	// the user picked it. The flow tracks that in memory.
	languageConfirmed: boolean
}

export type TrialSetupProgress = {
	hasCompany: boolean
	hasBillingDetails: boolean
	hasCard: boolean
	// The trial is how a self-signup gets access, but an organisation may already have it another way.
	hasAccess: boolean
}

const QUESTION_STEPS = [
	'organisation_headcount',
	'manages_team',
	'contacts_candidates',
	'recruiting_volume',
	'phone_number',
] as const

const INVITED_USER_STEPS = ['language', ...QUESTION_STEPS] as const

// A self-signup creates their company and starts a free trial, so they can use AIR without talking to sales.
const SELF_SIGNUP_STEPS = ['language', 'company', ...QUESTION_STEPS, 'billing', 'free_trial'] as const

export type OnboardingScreen = typeof SELF_SIGNUP_STEPS[number]

export type OnboardingStep = OnboardingScreen | 'complete'

export function onboardingSteps(isSelfSignup: boolean): readonly OnboardingScreen[] {
	return isSelfSignup ? SELF_SIGNUP_STEPS : INVITED_USER_STEPS
}

export function nextUnansweredStep(user: OnboardingUser, trialSetup: TrialSetupProgress): OnboardingStep {
	if (user.onboardingCompletedAt !== null) { return 'complete' }
	if (!user.languageConfirmed && !hasAnsweredPastLanguage(user, trialSetup)) { return 'language' }
	if (user.isSelfSignup && !trialSetup.hasCompany) { return 'company' }
	if (user.organisationHeadcount === null) { return 'organisation_headcount' }
	if (user.managesTeam === null) { return 'manages_team' }
	if (user.contactsCandidates === null) { return 'contacts_candidates' }
	if (user.recruitingVolume === null) { return 'recruiting_volume' }
	// For an invited user the phone number is the final step, and submitting it completes onboarding.
	if (!user.isSelfSignup || user.phoneNumber === null) { return 'phone_number' }
	if (!trialSetup.hasBillingDetails || !trialSetup.hasCard) { return 'billing' }
	if (!trialSetup.hasAccess) { return 'free_trial' }
	return 'complete'
}

// A self-signup may also have been invited into a sales customer's company, which cannot trial; the list is sorted by name.
export function trialOrganisation(organisations: readonly OrganisationData[]): OrganisationData | null {
	return organisations.find((organisation) => !organisation.isSalesDriven && !organisation.hasParent)
		?? organisations[0]
		?? null
}

// The step after the language is answered, so the language was confirmed on an earlier visit.
function hasAnsweredPastLanguage(user: OnboardingUser, trialSetup: TrialSetupProgress): boolean {
	return user.isSelfSignup ? trialSetup.hasCompany : user.organisationHeadcount !== null
}
